import { GoogleGenAI, Type } from "@google/genai";
import { DecodedCharge, ChargeVerificationResult, AiSearchResult, GroundingSource } from '../types';

// ==================================================================================
// 🔑 ENTER YOUR API KEY BELOW
// ==================================================================================
// Paste your Gemini API Key inside the quotes below.
// You can get a key from: https://aistudio.google.com/app/apikey
const HARDCODED_API_KEY = 'AIzaSyCq6gnrLmHJ5WdLo6W8HFTKH90E5TDfTNg'; 
// ==================================================================================


// Helper function to get the API key from the correct source.
const getApiKey = (): string => {
    // 1. Priority: Check the hardcoded key in this file
    if (HARDCODED_API_KEY) {
        return HARDCODED_API_KEY;
    }

    // 2. Check environment variable (injected by build platform)
    if (process.env.API_KEY) {
        return process.env.API_KEY;
    }
    
    // 3. Fallback: Check localStorage (for local testing if hardcode is empty)
    const localApiKey = localStorage.getItem('GEMINI_API_KEY');
    if (localApiKey) {
        return localApiKey;
    }
    
    return '';
};

const NO_API_KEY_ERROR_DECODED: DecodedCharge = {
    fullName: "API Key Missing",
    meaning: "The AI service is not configured.",
    example: "Please add your API Key in services/geminiService.ts",
    tips: "Contact the website administrator."
};

const NO_API_KEY_ERROR_AI_SEARCH: AiSearchResult = {
    text: "### API Key Missing\nThe AI service is not configured. Please add your API Key in `services/geminiService.ts` to enable this feature.",
    sources: []
};

const responseSchema = {
    type: Type.OBJECT,
    properties: {
        fullName: {
            type: Type.STRING,
            description: 'The full name or expansion of the charge code.',
        },
        meaning: {
            type: Type.STRING,
            description: 'A clear explanation of what this charge means in the context of the user\'s selected country.',
        },
        example: {
            type: Type.STRING,
            description: 'A real-world example of when this charge might occur on a bank statement in that country.',
        },
        tips: {
            type: Type.STRING,
            description: 'Actionable tips for the user, such as how to avoid the charge or what to do about it.',
        },
    },
    required: ['fullName', 'meaning', 'example', 'tips'],
};


export const decodeChargeWithGemini = async (chargeCode: string, countryName: string): Promise<DecodedCharge> => {
    const apiKey = getApiKey();
    if (!apiKey) {
        console.error("Gemini API key not found.");
        return NO_API_KEY_ERROR_DECODED;
    }

    try {
        const ai = new GoogleGenAI({ apiKey });

        const prompt = `You are an expert on banking and finance for ${countryName}. Your task is to explain a bank statement charge code. The code is often an abbreviation.

    Charge Code: "${chargeCode}"
    
    Provide a concise, clear, and accurate explanation for a user in ${countryName}. Return the output as a valid JSON object matching the provided schema. If the code is ambiguous or could mean multiple things, provide the most common interpretation for ${countryName}.`;

        const response = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: prompt,
            config: {
                responseMimeType: 'application/json',
                responseSchema: responseSchema,
            },
        });

        const jsonText = response.text.trim();
        const decodedData: DecodedCharge = JSON.parse(jsonText);
        return decodedData;

    } catch (error) {
        console.error("Error decoding charge with Gemini:", error);
        return {
            fullName: "Error Decoding",
            meaning: `Could not decode the charge code "${chargeCode}". This might be a temporary issue with the AI service or an invalid/uncommon code.`,
            example: "N/A",
            tips: "Please check the charge code for typos and try again. If the problem persists, it may be a bank-specific code; in that case, contacting your bank is the best option."
        };
    }
};


const verificationSchema = {
  type: Type.OBJECT,
  properties: {
    isValid: {
      type: Type.BOOLEAN,
      description: 'Whether the submitted charge is a plausible financial charge.'
    },
    feedback: {
      type: Type.STRING,
      description: 'Concise feedback for the user. If valid, say "This looks accurate and has been refined.". If invalid, explain why (e.g., "This does not seem like a real bank charge.").'
    },
    refinedData: {
      type: Type.OBJECT,
      properties: {
        fullName: { type: Type.STRING, description: 'The corrected or verified full name of the charge.' },
        whatItMeans: { type: Type.STRING, description: 'A refined, clear, and concise explanation of what the charge means.' },
        whyCharged: { type: Type.STRING, description: 'A refined explanation of why this charge is levied.' },
        howToAvoid: { type: Type.ARRAY, items: { type: Type.STRING }, description: 'A refined list of actionable tips to avoid this charge.' }
      },
      required: ['fullName', 'whatItMeans', 'whyCharged', 'howToAvoid'],
    }
  },
  required: ['isValid', 'feedback', 'refinedData']
};

export const verifyChargeWithGemini = async (formData: any, countryName: string): Promise<ChargeVerificationResult | null> => {
    const apiKey = getApiKey();
    if (!apiKey) {
        console.error("Gemini API key not found.");
        return null;
    }
    try {
        const ai = new GoogleGenAI({ apiKey });

        const prompt = `You are a financial expert reviewing user-submitted bank charges for the ChargesDecoder website. Your task is to validate and refine the submission for accuracy and clarity for users in ${countryName}.

    Here is the user's submission:
    - Charge Code: "${formData.code}"
    - Charge Name: "${formData.name}"
    - Full Name: "${formData.fullName}"
    - Category: "${formData.category}"
    - Typical Amount: "${formData.typicalAmount}"
    - What it means: "${formData.whatItMeans}"
    - Why it's charged: "${formData.whyCharged}"
    - How to avoid it (one tip per line): "${formData.howToAvoid}"

    Please perform the following steps:
    1.  **Validate:** Determine if this is a plausible and realistic financial charge. It should not be a joke or nonsensical. Critically, consider if the 'Typical Amount' is reasonable for this type of charge in ${countryName}.
    2.  **Refine:** If it is valid, review the user-provided 'Full Name', 'What it means', 'Why it's charged', and 'How to avoid it' fields. Use the user's text as the primary source. Your goal is to correct any factual errors and improve grammar, but preserve the user's original intent and phrasing as much as possible. For the 'How to avoid it' section, ensure the tips are distinct, actionable, and not repetitive, merging or removing redundant advice. If the user's input is already excellent, you must keep it.
    3.  **Respond:** Return a JSON object matching the provided schema.
        - Set \`isValid\` to \`true\` if the charge is plausible, otherwise \`false\`.
        - Provide brief, direct \`feedback\` for the user.
        - Populate \`refinedData\` with your improved text. If the charge is invalid, you can return the original text.`;

        const response = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: prompt,
            config: {
                responseMimeType: 'application/json',
                responseSchema: verificationSchema,
            },
        });

        const jsonText = response.text.trim();
        return JSON.parse(jsonText);

    } catch (error) {
        console.error("Error verifying charge with Gemini:", error);
        return null;
    }
};

export const getAiAnswer = async (question: string, countryName: string): Promise<AiSearchResult> => {
    const apiKey = getApiKey();
    if (!apiKey) {
        console.error("Gemini API key not found.");
        return NO_API_KEY_ERROR_AI_SEARCH;
    }

    try {
        const ai = new GoogleGenAI({ apiKey });
        
        const prompt = `You are "Chargeie", a friendly and knowledgeable AI assistant for the ChargesDecoder website. Your expertise is in banking, finance, and transaction charges, specifically for users in ${countryName}.
    - Your primary goal is to provide accurate, up-to-date information. Use your search tool when the user asks about recent events, specific numbers, or current regulations.
    - Your tone should be helpful, clear, and easy for a non-expert to understand.
    - Do not provide financial advice. Stick to explaining concepts, charges, and financial terms.
    - Use markdown for formatting, such as lists for steps, tables for comparisons, and bold text for emphasis.
    - If a user asks something outside your scope (e.g., medical advice, random trivia), politely decline and steer them back to financial topics.
    
    Please answer the following user question: "${question}"`;

        const response = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: prompt,
            config: {
                tools: [{googleSearch: {}}],
            },
        });

        const text = response.text;
        // FIX: The type `GroundingChunk` from the API has optional properties, while `GroundingSource` requires them.
        // Filter for chunks that have a valid web source and then map to the correct type.
        const groundingChunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
        const sources: GroundingSource[] = groundingChunks
          .filter(chunk => chunk.web?.uri && chunk.web?.title)
          .map(chunk => ({
            web: {
              uri: chunk.web!.uri!,
              title: chunk.web!.title!,
            },
          }));

        return { text, sources };
    } catch (error) {
        console.error("Error getting AI answer from Gemini:", error);
        return {
            text: "Sorry, I encountered an error while trying to answer your question. Please try again in a moment.",
            sources: [],
        };
    }
};

export const getAiSearchResults = async (query: string, countryName: string): Promise<AiSearchResult> => {
    const apiKey = getApiKey();
    if (!apiKey) {
        console.error("Gemini API key not found.");
        return NO_API_KEY_ERROR_AI_SEARCH;
    }

    try {
        const ai = new GoogleGenAI({ apiKey });

        const prompt = `You are "Chargeie", an AI assistant for the ChargesDecoder website, a financial charge database. A user has searched for "${query}" in ${countryName}.

    1.  First, determine the user's intent. Are they looking for a specific charge code, a financial concept, current news/rates, or comparing options?
    2.  Use your search tool to find the most current and relevant information, especially for questions about rates, regulations, or recent financial news.
    3.  Provide a direct, comprehensive answer to their query based on search results.
    4.  If it seems like a charge code, explain what it is, why it's charged, and how to avoid it.
    5.  Use markdown for formatting (bold, lists, tables).
    6.  Keep the tone helpful and easy to understand for a non-expert. Do not give financial advice.
    7.  If the query is ambiguous, provide the most likely interpretation. If the query is nonsensical or completely unrelated to finance, politely state that you can only answer financial questions.`;

        const response = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: prompt,
            config: {
                tools: [{googleSearch: {}}],
            },
        });

        const text = response.text;
        // FIX: The type `GroundingChunk` from the API has optional properties, while `GroundingSource` requires them.
        // Filter for chunks that have a valid web source and then map to the correct type.
        const groundingChunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
        const sources: GroundingSource[] = groundingChunks
          .filter(chunk => chunk.web?.uri && chunk.web?.title)
          .map(chunk => ({
            web: {
              uri: chunk.web!.uri!,
              title: chunk.web!.title!,
            },
          }));

        return { text, sources };
    } catch (error) {
        console.error("Error getting AI search results from Gemini:", error);
        return {
            text: "Sorry, I encountered an error while processing your search. Please try again in a moment.",
            sources: [],
        };
    }
};